import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Helmet } from 'react-helmet-async';
import './styles/Signup.css';

const Signup = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const { LogIn } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (localStorage.getItem('userId')) {
      navigate('/profil');
    }
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const { name, email, password, confirmPassword } = formData;

    if (!name || !email || !password || !confirmPassword) {
      setError('Tous les champs sont requis.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(email)) {
      setError("L'adresse email n'est pas valide.");
      return;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(password)) {
      setError(
        'Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial.'
      );
      return;
    }

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    const userData = { name, email, password };

    setLoading(true);

    try {
      const response = await fetch(`${apiUrl}/sign-up`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || `Erreur ${response.status}: ${response.statusText}`);
      }

      LogIn(data.userId);
      localStorage.setItem('user', JSON.stringify(true));
      localStorage.setItem('userId', data.userId);

      alert('Inscription réussie !');

      setFormData({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
      });

      navigate('/profil');
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="signup">
      <Helmet>
        <title>Créer un compte - WinlyDeal</title>
        
        <meta 
          name="description" 
          content="Créez votre compte gratuitement sur WinlyDeal pour participer à nos offres exclusives et tenter de remporter vos produits préférés en contribuant." 
        />
        <meta 
          name="keywords" 
          content="inscription, compte, WinlyDeal, participer, deals exclusifs, gagner des produits" 
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://winlydeal.com/signup" />

        <meta property="og:title" content="Créer un compte - WinlyDeal" />
        <meta property="og:description" content="Inscrivez-vous sur WinlyDeal pour accéder à des deals uniques et tenter de remporter des produits en contribuant." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://winlydeal.com/signup" />
        <meta property="og:image" content="https://winlydeal.com/images/og-image.jpg" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Créer un compte - WinlyDeal" />
        <meta name="twitter:description" content="Inscrivez-vous et participez aux meilleures offres WinlyDeal pour gagner des produits en contribuant." />
        <meta name="twitter:image" content="https://winlydeal.com/images/og-image.jpg" />

        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "WebPage",
              "name": "Créer un compte - WinlyDeal",
              "description": "Créez votre compte gratuitement sur WinlyDeal et participez à des offres exclusives pour remporter des produits.",
              "url": "https://winlydeal.com/signup"
            }
          `}
        </script>
      </Helmet>

      <section className="signup-container">
        <h1 className="signup-title">Créer un compte</h1>

        {error && <p className="signup-error">{error}</p>}

        <form className="signup-form" onSubmit={handleSubmit} noValidate>
          <label className="signup-label" htmlFor="name">Nom</label>
          <input
            type="text"
            id="name"
            name="name"
            className="signup-input"
            placeholder="Entrez votre nom"
            value={formData.name}
            onChange={handleInputChange}
            required
          />

          <label className="signup-label" htmlFor="email">Adresse e-mail</label>
          <input
            type="email"
            id="email"
            name="email"
            className="signup-input"
            placeholder="Entrez votre adresse e-mail"
            value={formData.email}
            onChange={handleInputChange}
            required
          />

          <label className="signup-label" htmlFor="password">Mot de passe</label>
          <input
            type="password"
            id="password"
            name="password"
            className="signup-input"
            placeholder="Entrez votre mot de passe"
            value={formData.password}
            onChange={handleInputChange}
            required
          />

          <label className="signup-label" htmlFor="confirmPassword">Confirmez le mot de passe</label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            className="signup-input"
            placeholder="Confirmez votre mot de passe"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            required
          />

          <button type="submit" className="signup-button" disabled={loading}>
            {loading ? "Inscription en cours..." : "S'inscrire"}
          </button>
        </form>
      </section>
    </main>
  );
};

export default Signup;
